import React, { useCallback, useEffect, useState, useRef, useContext, Fragment } from 'react';
import { LanguageContext } from '../../App';
import { PhoneIcon, MessageSquareIcon, MapPinIcon, CarIcon, UserPlusIcon, MoveIcon, StarIcon, PackageIcon, ClipboardIcon, RefreshCwIcon, ArrowRightIcon, ClockIcon, CheckCircleIcon, ArrowUpIcon, AlertCircleIcon, MinimizeIcon, MaximizeIcon, XIcon, ChevronDownIcon, ChevronUpIcon, UserIcon, ZoomInIcon, ZoomOutIcon, LayoutDashboardIcon, TruckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, Tooltip as LeafletTooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Tooltip } from '../ui/Tooltip';
import { QuickActions } from './QuickActions';
import { DriverList } from './DriverList';
// OpenRouteService API key
const ORS_API_KEY = '5b3ce3597851110001cf6248c85e7211fc8a4d0bbdf017b753e67357';
// Enhanced type definitions
type Coordinates = [number, number];
type RouteInfo = {
  coordinates: Coordinates[];
  duration: number; // in minutes
  distance: number; // in kilometers
};
type StopRouteInfo = {
  eta: number; // in minutes
  distance: number; // in kilometers
};
type DeliveryLocation = {
  coordinates: Coordinates;
  address: string;
  area: string;
  block: string;
  city: string;
  country: string;
  stopNumber?: number;
  completed?: boolean;
  routeInfo?: StopRouteInfo;
};
type OrderType = {
  id: string;
  shipmentId: string;
  customer: string;
  status: string;
  location: Coordinates; // pickup location
  deliveryLocations: DeliveryLocation[];
  items: Array<{
    name: string;
    category: string;
    price: string;
    quantity: number;
  }>;
  pickupLocation: {
    address: string;
    area: string;
    block: string;
    city: string;
    country: string;
  };
  timeline: Array<{
    status: string;
    title: string;
    time: string;
    date: string;
    location: string;
  }>;
  driver?: {
    id: string;
    name: string;
    vehicle: string;
    vehicleId: string;
    phone: string;
    rating: number;
    fuelLevel: number;
  };
  estimatedDelivery: {
    date: string;
    timeWindow: string;
  };
  totalTime: string;
  routeInfo?: RouteInfo;
};
type DriverType = {
  id: number;
  name: string;
  status: string;
  location: Coordinates;
  vehicle: string;
  rating: number;
  ordersCompleted: number;
  phone: string;
  avatar: string;
  routeInfo?: RouteInfo;
};
// Add these types to the existing type definitions
type DeliverySpeed = 'fast' | 'slowed' | 'jammed';
// RouteDrawer component to fetch and display routes
const RouteDrawer = ({
  startPoint,
  endPoints,
  color = '#0097ff',
  weight = 4,
  opacity = 0.7,
  dashArray = '',
  onRouteCalculated
}: {
  startPoint: Coordinates;
  endPoints: Coordinates[];
  color?: string;
  weight?: number;
  opacity?: number;
  dashArray?: string;
  onRouteCalculated?: (routeInfo: RouteInfo) => void;
}) => {
  const [routeData, setRouteData] = useState<RouteInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastFetchRef = useRef<string>('');
  const fetchRoute = useCallback(async () => {
    if (!startPoint || !endPoints.length) return;
    // Create a signature for this request to avoid duplicate fetches
    const signature = JSON.stringify({
      start: startPoint,
      ends: endPoints
    });
    if (signature === lastFetchRef.current) return;
    lastFetchRef.current = signature;
    setIsLoading(true);
    setError(null);
    try {
      // For multi-stop routes, we need to make multiple requests
      // and stitch the results together
      let allCoordinates: Coordinates[] = [];
      let totalDuration = 0;
      let totalDistance = 0;
      // Start with the route from start to first endpoint
      let currentPoint = startPoint;
      for (const endPoint of endPoints) {
        // Format coordinates for ORS API (longitude first, then latitude)
        const start = `${currentPoint[1]},${currentPoint[0]}`;
        const end = `${endPoint[1]},${endPoint[0]}`;
        const response = await fetch(`https://api.openrouteservice.org/v2/directions/driving-car?api_key=${ORS_API_KEY}&start=${start}&end=${end}`);
        if (!response.ok) {
          throw new Error(`Error fetching route: ${response.statusText}`);
        }
        const data = await response.json();
        if (!data.features || !data.features.length) {
          throw new Error('No route found');
        }
        const route = data.features[0];
        const geometry = route.geometry;
        const summary = route.properties.summary;
        // Convert GeoJSON coordinates to Leaflet coordinates [lat, lng]
        const segmentCoordinates: Coordinates[] = decodePolyline(geometry).map(coord => [coord[1], coord[0]] as Coordinates);
        // Add all coordinates except the first one (to avoid duplicates)
        if (allCoordinates.length === 0) {
          allCoordinates = segmentCoordinates;
        } else {
          allCoordinates = [...allCoordinates, ...segmentCoordinates.slice(1)];
        }
        // Add duration and distance
        totalDuration += summary.duration;
        totalDistance += summary.distance;
        // Update current point to the end point for the next segment
        currentPoint = endPoint;
      }
      const routeInfo: RouteInfo = {
        coordinates: allCoordinates,
        duration: Math.round(totalDuration / 60),
        distance: +(totalDistance / 1000).toFixed(1) // Convert meters to kilometers
      };
      setRouteData(routeInfo);
      if (onRouteCalculated) {
        onRouteCalculated(routeInfo);
      }
    } catch (err) {
      console.error('Error fetching route:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [startPoint, endPoints, onRouteCalculated]);
  useEffect(() => {
    fetchRoute();
    // Set up periodic refresh every 60 seconds
    const intervalId = setInterval(fetchRoute, 60000);
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchRoute]);
  if (error) {
    console.error('Route error:', error);
    // Fallback to straight line if route calculation fails
    return <Polyline positions={[startPoint, ...endPoints]} pathOptions={{
      color,
      weight,
      opacity,
      dashArray
    }} data-id="element-69" />;
  }
  if (!routeData) {
    // Show a placeholder while loading
    return <Polyline positions={[startPoint, ...endPoints]} pathOptions={{
      color,
      weight: 1,
      opacity: 0.3,
      dashArray: '5,10'
    }} data-id="element-70" />;
  }
  return <Polyline positions={routeData.coordinates} pathOptions={{
    color,
    weight,
    opacity,
    dashArray
  }} data-id="element-71">
      {routeData.duration && routeData.distance && <LeafletTooltip sticky data-id="element-72">
          <div className="text-xs" data-id="element-73">
            <div className="font-medium" data-id="element-74">ETA: {routeData.duration} min</div>
            <div data-id="element-75">Distance: {routeData.distance} km</div>
          </div>
        </LeafletTooltip>}
    </Polyline>;
};
// Helper function to decode the polyline from ORS API
function decodePolyline(encodedPolyline: string): [number, number][] {
  // This is a simplified polyline decoder
  // In a production app, you would use a proper library
  let index = 0;
  let lat = 0;
  let lng = 0;
  const coordinates: [number, number][] = [];
  const len = encodedPolyline.length;
  while (index < len) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encodedPolyline.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = encodedPolyline.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng;
    coordinates.push([lat / 1e5, lng / 1e5]);
  }
  return coordinates;
}
// Define custom marker icons
const createCustomIcon = (html: string) => {
  return L.divIcon({
    html,
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  });
};
// Reset map view control
const ResetMapView = ({
  center,
  zoom,
  onToggleOperations,
  showOperations,
  mapWrapperRef
}: {
  center: Coordinates;
  zoom: number;
  onToggleOperations?: () => void;
  showOperations?: boolean;
  mapWrapperRef: React.RefObject<HTMLDivElement>;
}) => {
  const {
    language
  } = useContext(LanguageContext);
  const map = useMap();
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Check fullscreen state on mount and when fullscreen changes
  useEffect(() => {
    const checkFullscreen = () => {
      const isDocFullscreen = !!document.fullscreenElement || !!(document as any).webkitFullscreenElement || !!(document as any).mozFullScreenElement || !!(document as any).msFullscreenElement;
      setIsFullscreen(isDocFullscreen);
      // Force map to redraw after fullscreen change with a delay
      // to ensure the browser has completed the fullscreen transition
      if (map) {
        // First immediate invalidation
        map.invalidateSize();
        // Second invalidation after transition should be complete
        setTimeout(() => {
          map.invalidateSize(true);
        }, 400);
      }
    };
    // Add all browser-specific fullscreen change events
    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('mozfullscreenchange', checkFullscreen);
    document.addEventListener('MSFullscreenChange', checkFullscreen);
    // Initial check
    checkFullscreen();
    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('mozfullscreenchange', checkFullscreen);
      document.removeEventListener('MSFullscreenChange', checkFullscreen);
    };
  }, [map]);
  const resetView = () => {
    if (map) {
      map.setView(center, zoom);
    }
  };
  const handleZoomIn = () => {
    if (map) {
      map.setZoom(map.getZoom() + 1);
    }
  };
  const handleZoomOut = () => {
    if (map) {
      map.setZoom(map.getZoom() - 1);
    }
  };
  const toggleFullscreen = () => {
    // Use the mapWrapperRef from props to get the correct element
    const mapContainer = mapWrapperRef?.current;
    if (!mapContainer) {
      console.error('Map container not found');
      return;
    }
    try {
      if (!isFullscreen) {
        // Enter fullscreen mode
        if (mapContainer.requestFullscreen) {
          mapContainer.requestFullscreen();
        } else if ((mapContainer as any).mozRequestFullScreen) {
          ;
          (mapContainer as any).mozRequestFullScreen();
        } else if ((mapContainer as any).webkitRequestFullscreen) {
          ;
          (mapContainer as any).webkitRequestFullscreen();
        } else if ((mapContainer as any).msRequestFullscreen) {
          ;
          (mapContainer as any).msRequestFullscreen();
        }
        // Force map resize after a delay to ensure browser has transitioned to fullscreen
        setTimeout(() => {
          if (map) map.invalidateSize(true);
        }, 200);
      } else {
        // Exit fullscreen mode
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          ;
          (document as any).mozCancelFullScreen();
        } else if ((document as any).webkitExitFullscreen) {
          ;
          (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          ;
          (document as any).msExitFullscreen();
        }
        // Force map resize after exiting fullscreen
        setTimeout(() => {
          if (map) map.invalidateSize(true);
        }, 200);
      }
    } catch (error) {
      console.error('Fullscreen API error:', error);
    }
  };
  return <div className="leaflet-top leaflet-right" style={{
    zIndex: 1000
  }} data-id="element-76">
      <div className="leaflet-control leaflet-bar" data-id="element-77">
        <div className="flex flex-col gap-1 p-1 bg-white rounded-md shadow-md" data-id="element-78">
          {/* Delivery Operations Toggle Button */}
          {onToggleOperations && <Tooltip content={language === 'en' ? showOperations ? 'Hide Delivery Operations' : 'Show Delivery Operations' : showOperations ? 'إخفاء عمليات التوصيل' : 'إظهار عمليات التوصيل'} side="left" data-id="element-79">
              <button onClick={onToggleOperations} className={`p-2 transition-colors rounded-md ${showOperations ? 'bg-congress-blue-50 text-congress-blue-600' : 'hover:bg-gray-50 text-gray-700'}`} aria-label={language === 'en' ? showOperations ? 'Hide Delivery Operations' : 'Show Delivery Operations' : showOperations ? 'إخفاء عمليات التوصيل' : 'إظهار عمليات التوصيل'} data-id="element-80">
                <LayoutDashboardIcon size={18} data-id="element-81" />
              </button>
            </Tooltip>}
          {/* Zoom In */}
          <Tooltip content={language === 'en' ? 'Zoom In' : 'تكبير'} side="left" data-id="element-82">
            <button onClick={handleZoomIn} className="p-2 hover:bg-gray-50 transition-colors rounded-md" aria-label={language === 'en' ? 'Zoom In' : 'تكبير'} data-id="element-83">
              <ZoomInIcon size={18} className="text-gray-700" data-id="element-84" />
            </button>
          </Tooltip>
          {/* Zoom Out */}
          <Tooltip content={language === 'en' ? 'Zoom Out' : 'تصغير'} side="left" data-id="element-85">
            <button onClick={handleZoomOut} className="p-2 hover:bg-gray-50 transition-colors rounded-md" aria-label={language === 'en' ? 'Zoom Out' : 'تصغير'} data-id="element-86">
              <ZoomOutIcon size={18} className="text-gray-700" data-id="element-87" />
            </button>
          </Tooltip>
          {/* Reset View */}
          <Tooltip content={language === 'en' ? 'Reset View' : 'إعادة تعيين العرض'} side="left" data-id="element-88">
            <button onClick={resetView} className="p-2 hover:bg-gray-50 transition-colors rounded-md" aria-label={language === 'en' ? 'Reset View' : 'إعادة تعيين العرض'} data-id="element-89">
              <MoveIcon size={18} className="text-gray-700" data-id="element-90" />
            </button>
          </Tooltip>
          {/* Fullscreen Toggle */}
          <Tooltip content={isFullscreen ? language === 'en' ? 'Exit Fullscreen' : 'الخروج من وضع ملء الشاشة' : language === 'en' ? 'Enter Fullscreen' : 'تفعيل وضع ملء الشاشة'} side="left" data-id="element-91">
            <button onClick={toggleFullscreen} className="p-2 hover:bg-gray-50 transition-colors rounded-md" aria-label={isFullscreen ? language === 'en' ? 'Exit Fullscreen' : 'الخروج من وضع ملء الشاشة' : language === 'en' ? 'Enter Fullscreen' : 'تفعيل وضع ملء الشاشة'} data-id="element-92">
              {isFullscreen ? <MinimizeIcon size={18} className="text-gray-700" data-id="element-93" /> : <MaximizeIcon size={18} className="text-gray-700" data-id="element-94" />}
            </button>
          </Tooltip>
        </div>
      </div>
    </div>;
};
// Sample orders data with multiple delivery locations
const orders: OrderType[] = [{
  id: 'ORD-7829',
  shipmentId: 'SHP20240130001',
  customer: 'Ahmed Al-Sabah',
  status: 'in_transit',
  location: [29.3759, 47.9774],
  deliveryLocations: [{
    coordinates: [29.3559, 47.9874],
    address: '1352 Elm Street',
    area: 'Hawally',
    block: 'Block 3',
    city: 'Kuwait City',
    country: 'Kuwait',
    stopNumber: 1,
    completed: false
  }, {
    coordinates: [29.3659, 47.9974],
    address: '785 Palm Avenue',
    area: 'Salmiya',
    block: 'Block 6',
    city: 'Kuwait City',
    country: 'Kuwait',
    stopNumber: 2,
    completed: false
  }],
  items: [{
    name: 'iPhone 15 Pro',
    category: 'Electronics',
    price: 'KWD 350.00',
    quantity: 1
  }],
  pickupLocation: {
    address: '8429 Maple Lane',
    area: 'Salmiya',
    block: 'Block 10',
    city: 'Kuwait City',
    country: 'Kuwait'
  },
  timeline: [{
    status: 'delivered',
    title: 'Delivered to destination',
    time: '09:15',
    date: 'Nov 19, 2024',
    location: 'Hawally, Block 3, Kuwait'
  }, {
    status: 'at_local_depot',
    title: 'Arrived at local depot',
    time: '10:21',
    date: 'Nov 18, 2024',
    location: 'Kuwait City Distribution Center'
  }, {
    status: 'in_transit',
    title: 'In transit with carrier',
    time: '17:04',
    date: 'Nov 17, 2024',
    location: 'En route to Kuwait City'
  }, {
    status: 'package_confirmed',
    title: 'Package confirmed',
    time: '12:18',
    date: 'Nov 17, 2024',
    location: 'Salmiya Processing Center'
  }],
  driver: {
    id: 'DRV-1001',
    name: 'Rahul Sharma',
    vehicle: 'Toyota Hilux',
    vehicleId: 'KWT 70757',
    phone: '+965 9876 1234',
    rating: 4.9,
    fuelLevel: 87
  },
  estimatedDelivery: {
    date: 'Nov 19, 2024',
    timeWindow: '09:00 - 11:00'
  },
  totalTime: '14 hr 20 min'
}, {
  id: 'ORD-7828',
  shipmentId: 'SHP20240130002',
  customer: 'Fatima Al-Qadiri',
  status: 'delivered',
  location: [29.3659, 47.9674],
  deliveryLocations: [{
    coordinates: [29.3859, 47.9874],
    address: '456 Beach Road',
    area: 'Salmiya',
    block: 'Block 10',
    city: 'Kuwait City',
    country: 'Kuwait',
    stopNumber: 1,
    completed: true
  }],
  items: [{
    name: 'MacBook Air',
    category: 'Electronics',
    price: 'KWD 450.00',
    quantity: 1
  }],
  pickupLocation: {
    address: '123 Palm Street',
    area: 'Hawally',
    block: 'Block 5',
    city: 'Kuwait City',
    country: 'Kuwait'
  },
  timeline: [{
    status: 'delivered',
    title: 'Delivered to destination',
    time: '14:30',
    date: 'Nov 18, 2024',
    location: 'Salmiya, Block 10, Kuwait'
  }, {
    status: 'in_transit',
    title: 'In transit with carrier',
    time: '12:45',
    date: 'Nov 18, 2024',
    location: 'En route to Salmiya'
  }, {
    status: 'package_confirmed',
    title: 'Package confirmed',
    time: '09:20',
    date: 'Nov 18, 2024',
    location: 'Hawally Processing Center'
  }],
  driver: {
    id: 'DRV-1002',
    name: 'Ahmed Khan',
    vehicle: 'Honda Civic',
    vehicleId: 'KWT 12345',
    phone: '+965 9876 5678',
    rating: 4.7,
    fuelLevel: 65
  },
  estimatedDelivery: {
    date: 'Nov 18, 2024',
    timeWindow: '14:00 - 16:00'
  },
  totalTime: '5 hr 10 min'
}, {
  id: 'ORD-7827',
  shipmentId: 'SHP20240130003',
  customer: 'Mohammed Al-Mutairi',
  status: 'delayed',
  location: [29.3959, 47.9674],
  deliveryLocations: [{
    coordinates: [29.3559, 47.9574],
    address: '321 Pine Street',
    area: 'Salmiya',
    block: 'Block 2',
    city: 'Kuwait City',
    country: 'Kuwait',
    stopNumber: 1,
    completed: false
  }, {
    coordinates: [29.3459, 47.9674],
    address: '567 Cedar Lane',
    area: 'Kuwait City',
    block: 'Block 4',
    city: 'Kuwait City',
    country: 'Kuwait',
    stopNumber: 2,
    completed: false
  }, {
    coordinates: [29.3359, 47.9774],
    address: '890 Oak Boulevard',
    area: 'Hawally',
    block: 'Block 7',
    city: 'Kuwait City',
    country: 'Kuwait',
    stopNumber: 3,
    completed: false
  }],
  items: [{
    name: 'Samsung Galaxy S24',
    category: 'Electronics',
    price: 'KWD 300.00',
    quantity: 1
  }],
  pickupLocation: {
    address: '789 Oak Avenue',
    area: 'Jabriya',
    block: 'Block 1',
    city: 'Kuwait City',
    country: 'Kuwait'
  },
  timeline: [{
    status: 'delayed',
    title: 'Delivery delayed',
    time: '16:45',
    date: 'Nov 19, 2024',
    location: 'Traffic jam in Kuwait City'
  }, {
    status: 'in_transit',
    title: 'In transit with carrier',
    time: '14:15',
    date: 'Nov 19, 2024',
    location: 'En route to Salmiya'
  }, {
    status: 'package_confirmed',
    title: 'Package confirmed',
    time: '11:30',
    date: 'Nov 19, 2024',
    location: 'Jabriya Processing Center'
  }],
  driver: {
    id: 'DRV-1003',
    name: 'Farhan Qureshi',
    vehicle: 'Toyota Corolla',
    vehicleId: 'KWT 67890',
    phone: '+965 9876 9012',
    rating: 4.8,
    fuelLevel: 42
  },
  estimatedDelivery: {
    date: 'Nov 19, 2024',
    timeWindow: '16:00 - 18:00'
  },
  totalTime: '7 hr 15 min'
}];
// Sample drivers data
const drivers: DriverType[] = [{
  id: 1,
  name: 'Rahul Sharma',
  status: 'available',
  location: [29.3759, 47.9774],
  vehicle: 'motorcycle',
  rating: 4.9,
  ordersCompleted: 145,
  phone: '+965 9876 1234',
  avatar: 'https://i.ibb.co/xG0Jcnm/driver1.jpg'
}, {
  id: 2,
  name: 'Ahmed Khan',
  status: 'on_delivery',
  location: [29.3659, 47.9674],
  vehicle: 'car',
  rating: 4.7,
  ordersCompleted: 89,
  phone: '+965 9876 5678',
  avatar: 'https://i.ibb.co/k2V6YPL/driver2.jpg'
}, {
  id: 3,
  name: 'Farhan Qureshi',
  status: 'offline',
  location: [29.3859, 47.9874],
  vehicle: 'motorcycle',
  rating: 4.8,
  ordersCompleted: 120,
  phone: '+965 9876 9012',
  avatar: 'https://i.ibb.co/0hFTSyy/driver3.jpg'
}, {
  id: 4,
  name: 'Arjun Patel',
  status: 'available',
  location: [29.3959, 47.9674],
  vehicle: 'car',
  rating: 4.6,
  ordersCompleted: 95,
  phone: '+965 9876 4567',
  avatar: 'https://i.ibb.co/wdQSrBL/driver4.jpg'
}, {
  id: 5,
  name: 'Imran Malik',
  status: 'on_delivery',
  location: [29.3559, 47.9874],
  vehicle: 'motorcycle',
  rating: 4.5,
  ordersCompleted: 78,
  phone: '+965 9876 7890',
  avatar: 'https://i.ibb.co/bRZmtTG/driver5.jpg'
}];
interface LiveMapProps {
  selectedDriver: any;
  onDriverSelect: (driver: any) => void;
  mapWrapperRef: React.RefObject<HTMLDivElement>;
  onToggleOperations?: () => void;
  showOperations?: boolean;
  isCardCollapsed?: boolean;
  setIsCardCollapsed?: (collapsed: boolean) => void;
}
export const LiveMap: React.FC<LiveMapProps> = ({
  selectedDriver,
  onDriverSelect,
  mapWrapperRef,
  onToggleOperations,
  showOperations,
  isCardCollapsed = true,
  setIsCardCollapsed
}) => {
  const {
    language
  } = useContext(LanguageContext);
  const [selectedOrder, setSelectedOrder] = useState<OrderType | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState<boolean>(false);
  const [mapError, setMapError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showDeliveryStatus, setShowDeliveryStatus] = useState<boolean>(false);
  const [deliveryStatusCollapsed, setDeliveryStatusCollapsed] = useState<boolean>(false);
  const [timelineCollapsed, setTimelineCollapsed] = useState<boolean>(false);
  const [orderRoutes, setOrderRoutes] = useState<{
    [key: string]: RouteInfo;
  }>({});
  const [selectedStop, setSelectedStop] = useState<{
    orderId: string;
    stopIndex: number;
  } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapRef = useRef<L.Map | null>(null);
  const stopRefs = useRef<{
    [key: string]: HTMLDivElement;
  }>({});
  const markersRef = useRef<{
    [key: string]: L.Marker;
  }>({});
  // Kuwait City coordinates
  const center: Coordinates = [29.3759, 47.9774];
  const zoom = 12;
  // Map reference setter
  const MapController = () => {
    const map = useMap();
    useEffect(() => {
      if (map) {
        mapRef.current = map;
      }
    }, [map]);
    return null;
  };
  // Handle order marker click
  const handleOrderMarkerClick = (order: OrderType) => {
    setSelectedOrder(order);
    setShowDeliveryStatus(true);
    setDeliveryStatusCollapsed(false); // Ensure card is expanded
    setTimelineCollapsed(false); // Ensure timeline is expanded
  };
  // Handle delivery stop marker click
  const handleStopMarkerClick = (orderId: string, stopIndex: number) => {
    setSelectedStop({
      orderId,
      stopIndex
    });
    // Select the parent order if not already selected
    const order = orders.find(o => o.id === orderId);
    if (order && (!selectedOrder || selectedOrder.id !== orderId)) {
      handleOrderMarkerClick(order);
    }
    // Scroll to the stop in the sidebar if it exists
    const stopKey = `stop-${orderId}-${stopIndex}`;
    if (stopRefs.current[stopKey]) {
      stopRefs.current[stopKey].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };
  // Handle stop click in sidebar
  const handleSidebarStopClick = (orderId: string, stopIndex: number, coordinates: Coordinates) => {
    setSelectedStop({
      orderId,
      stopIndex
    });
    // Pan and zoom to the marker
    if (mapRef.current) {
      mapRef.current.setView(coordinates, 14, {
        animate: true
      });
      // Open the popup if the marker reference exists
      const markerKey = `delivery-${orderId}-${stopIndex}`;
      if (markersRef.current[markerKey]) {
        markersRef.current[markerKey].openPopup();
      }
    }
  };
  // Toggle order details panel
  const toggleOrderDetails = () => {
    setShowOrderDetails(!showOrderDetails);
  };
  // Close delivery status panel
  const closeDeliveryStatus = () => {
    setShowDeliveryStatus(false);
    setSelectedStop(null);
  };
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-500';
      case 'on_delivery':
        return 'bg-yellow-500';
      case 'offline':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };
  // Get status text
  const getStatusText = (status: string) => {
    if (language === 'en') {
      switch (status) {
        case 'available':
          return 'Available';
        case 'on_delivery':
          return 'On Delivery';
        case 'offline':
          return 'Offline';
        default:
          return 'Unknown';
      }
    } else {
      switch (status) {
        case 'available':
          return 'متاح';
        case 'on_delivery':
          return 'في مهمة توصيل';
        case 'offline':
          return 'غير متصل';
        default:
          return 'غير معروف';
      }
    }
  };
  // Get timeline status icon
  const getTimelineIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <div className="w-8 h-8 rounded-full bg-orange-100 border-2 border-orange-500 flex items-center justify-center" data-id="element-95">
            <PackageIcon size={16} className="text-orange-600" data-id="element-96" />
          </div>;
      case 'at_local_depot':
        return <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center" data-id="element-97">
            <ArrowUpIcon size={16} className="text-gray-600" data-id="element-98" />
          </div>;
      case 'in_transit':
        return <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center" data-id="element-99">
            <ArrowUpIcon size={16} className="text-gray-600" data-id="element-100" />
          </div>;
      case 'package_confirmed':
        return <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center" data-id="element-101">
            <PackageIcon size={16} className="text-gray-600" data-id="element-102" />
          </div>;
      default:
        return <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center" data-id="element-103">
            <ClockIcon size={16} className="text-gray-600" data-id="element-104" />
          </div>;
    }
  };
  // Add this helper function with the existing ones
  const getDeliverySpeedColor = (speed: DeliverySpeed) => {
    switch (speed) {
      case 'fast':
        return 'bg-green-500';
      case 'slowed':
        return 'bg-congress-blue-500';
      case 'jammed':
        return 'bg-red-500';
      default:
        return 'bg-gray-300';
    }
  };
  // Add this to the existing functions
  const getDeliverySpeedFromStatus = (status: string): DeliverySpeed => {
    switch (status) {
      case 'delivered':
        return 'fast';
      case 'delayed':
        return 'jammed';
      case 'in_transit':
        return 'slowed';
      default:
        return 'fast';
    }
  };
  // Add this to the existing functions
  const getTimelineProgressPercent = (timeline: any[]) => {
    if (!timeline || timeline.length === 0) return 0;
    const completedSteps = timeline.filter(item => item.status === 'delivered' || item.status === 'at_local_depot' || item.status === 'package_confirmed').length;
    return Math.min(100, Math.round(completedSteps / timeline.length * 100));
  };
  // Create marker icons for different vehicle types and statuses
  const createVehicleIcon = (vehicle: string, status: string) => {
    const markerColor = status === 'available' ? '#10B981' // green
    : status === 'on_delivery' ? '#F59E0B' // orange
    : '#EF4444'; // red
    return L.divIcon({
      html: `
        <div style="background-color: white; width: 36px; height: 36px; border-radius: 50%; border: 3px solid ${markerColor}; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 5px rgba(0,0,0,0.2);">
          <div style="color: ${markerColor}; font-size: 18px; font-weight: bold;">
            ${vehicle === 'car' ? '🚗' : '🏍️'}
          </div>
        </div>
      `,
      className: '',
      iconSize: [36, 36],
      iconAnchor: [18, 18]
    });
  };
  // Create order marker icons for different status
  const createOrderIcon = (status: string) => {
    let statusColor = '#0097ff'; // Default congress-blue color
    switch (status) {
      case 'in_transit':
        statusColor = '#0097ff'; // congress-blue
        break;
      case 'delivered':
        statusColor = '#10B981'; // green
        break;
      case 'delayed':
        statusColor = '#F59E0B'; // yellow/orange
        break;
      case 'cancelled':
        statusColor = '#EF4444'; // red
        break;
      default:
        statusColor = '#0097ff';
    }
    const orderIconHtml = `
      <div style="position: relative; display: flex; align-items: center; justify-content: center;">
        <div style="width: 40px; height: 40px; background-color: white; border-radius: 50%; border: 2px solid ${statusColor}; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${statusColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M16 16h6"/><path d="M8 16h2"/><path d="M12 16h.01"/><path d="M3 9h18v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"/><path d="M3 9V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3"/><path d="M3 9h18"/>
          </svg>
        </div>
        <div style="position: absolute; bottom: -4px; right: -4px; width: 16px; height: 16px; border-radius: 50%; background-color: ${statusColor}; border: 2px solid white;"></div>
      </div>
    `;
    return createCustomIcon(orderIconHtml);
  };
  // Create delivery stop marker icons
  const createDeliveryStopIcon = (stopNumber: number, completed: boolean) => {
    const color = completed ? '#10B981' : '#0097ff';
    return L.divIcon({
      html: `
        <div style="position: relative; display: flex; align-items: center; justify-content: center;">
          <div style="width: 36px; height: 36px; background-color: white; border-radius: 50%; border: 2px solid ${color}; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
            <div style="color: ${color}; font-weight: bold; font-size: 14px;">${stopNumber}</div>
          </div>
          ${completed ? `<div style="position: absolute; bottom: -4px; right: -4px; width: 16px; height: 16px; border-radius: 50%; background-color: #10B981; border: 2px solid white; display: flex; align-items: center; justify-content: center;">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>` : ''}
        </div>
      `,
      className: '',
      iconSize: [36, 36],
      iconAnchor: [18, 18]
    });
  };
  // Create pickup location marker icon
  const pickupIcon = createCustomIcon(`
    <div style="position: relative; display: flex; align-items: center; justify-content: center;">
      <div style="width: 40px; height: 40px; background-color: white; border-radius: 50%; border: 2px solid #10B981; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
      </div>
    </div>
  `);
  // Handle route calculation results with per-stop ETAs
  const handleRouteCalculated = useCallback((orderId: string, routeInfo: RouteInfo) => {
    setOrderRoutes(prev => ({
      ...prev,
      [orderId]: routeInfo
    }));
    // Find the order and calculate per-stop ETAs
    const order = orders.find(o => o.id === orderId);
    if (order) {
      const updatedOrder = calculateStopRoutes(order, routeInfo);
      // Update the order in the orders array
      const orderIndex = orders.findIndex(o => o.id === orderId);
      if (orderIndex >= 0) {
        orders[orderIndex] = updatedOrder;
      }
    }
  }, []);
  // Calculate per-stop ETAs and distances
  const calculateStopRoutes = useCallback((order: OrderType, routeInfo: RouteInfo) => {
    if (!routeInfo || !routeInfo.coordinates || routeInfo.coordinates.length < 2) {
      return order;
    }
    // Create a deep copy of the order to avoid mutation
    const updatedOrder = {
      ...order,
      deliveryLocations: [...order.deliveryLocations]
    };
    // We need to find the route segments for each stop
    let currentIndex = 0;
    let currentCoord = order.location; // Start from pickup location
    for (let i = 0; i < updatedOrder.deliveryLocations.length; i++) {
      const stop = {
        ...updatedOrder.deliveryLocations[i]
      };
      const stopCoord = stop.coordinates;
      // Find the path to this stop in the route
      let segmentDistance = 0;
      let segmentDuration = 0;
      let foundStop = false;
      // Look through coordinates to find the segment ending at this stop
      for (let j = currentIndex; j < routeInfo.coordinates.length; j++) {
        const coord = routeInfo.coordinates[j];
        // Calculate distance between this coordinate and the stop
        const dist = L.latLng(coord[0], coord[1]).distanceTo(L.latLng(stopCoord[0], stopCoord[1]));
        // If we're close to the stop coordinate, we found the segment end
        if (dist < 100) {
          // Within 100 meters
          foundStop = true;
          // Calculate the proportion of the total route this segment represents
          const segmentLength = j - currentIndex + 1;
          const totalLength = routeInfo.coordinates.length;
          // Estimate duration and distance based on proportion of the route
          segmentDuration = Math.round(segmentLength / totalLength * routeInfo.duration);
          segmentDistance = +(segmentLength / totalLength * routeInfo.distance).toFixed(1);
          // Update current position for next segment
          currentIndex = j + 1;
          currentCoord = coord;
          break;
        }
      }
      // If we couldn't find the exact stop in the route, make a best guess
      if (!foundStop) {
        // Estimate based on the straight-line distance proportion
        const straightLineDist = L.latLng(currentCoord[0], currentCoord[1]).distanceTo(L.latLng(stopCoord[0], stopCoord[1]));
        const totalStraightLineDist = L.latLng(order.location[0], order.location[1]).distanceTo(L.latLng(stopCoord[0], stopCoord[1]));
        const proportion = straightLineDist / totalStraightLineDist || 0.5;
        segmentDuration = Math.round(proportion * routeInfo.duration);
        segmentDistance = +(proportion * routeInfo.distance).toFixed(1);
      }
      // Add the route info to the stop
      stop.routeInfo = {
        eta: segmentDuration,
        distance: segmentDistance
      };
      updatedOrder.deliveryLocations[i] = stop;
    }
    return updatedOrder;
  }, []);
  // Register marker reference
  const registerMarkerRef = useCallback((key: string, marker: L.Marker) => {
    if (marker) {
      markersRef.current[key] = marker;
    }
  }, []);
  // If there's an error with the map, show error state
  if (mapError) {
    return <div className="h-full w-full flex items-center justify-center bg-gray-50" data-id="element-105">
        <div className="text-center p-6 max-w-md" data-id="element-106">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-107">
            <AlertCircleIcon size={32} className="text-red-500" data-id="element-108" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-109">
            {language === 'en' ? 'Map could not be loaded' : 'تعذر تحميل الخريطة'}
          </h3>
          <p className="text-gray-600 mb-4" data-id="element-110">
            {language === 'en' ? errorMessage || 'There was an error loading the map. Please make sure you have an internet connection.' : 'حدث خطأ أثناء تحميل الخريطة. يرجى التأكد من اتصالك بالإنترنت.'}
          </p>
          <button className="px-4 py-2 bg-congress-blue-600 text-white rounded-md hover:bg-congress-blue-700 transition-colors" onClick={() => window.location.reload()} data-id="element-111">
            {language === 'en' ? 'Reload' : 'إعادة تحميل'}
          </button>
        </div>
      </div>;
  }
  // Force map to update its size on window resize and fullscreen change
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize(true);
      }
    };
    window.addEventListener('resize', handleResize);
    // Check for fullscreen changes
    const handleFullscreenChange = () => {
      const isDocFullscreen = !!document.fullscreenElement || !!(document as any).webkitFullscreenElement || !!(document as any).mozFullScreenElement || !!(document as any).msFullscreenElement;
      setIsFullscreen(isDocFullscreen);
      // Force multiple invalidations to ensure map renders correctly
      setTimeout(() => handleResize(), 100);
      setTimeout(() => handleResize(), 300);
      setTimeout(() => handleResize(), 500);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    // Initial size check after component mounts
    setTimeout(handleResize, 300);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);
  // Scroll to selected stop when it changes
  useEffect(() => {
    if (selectedStop) {
      const stopKey = `stop-${selectedStop.orderId}-${selectedStop.stopIndex}`;
      if (stopRefs.current[stopKey]) {
        stopRefs.current[stopKey].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, [selectedStop]);
  return <div className="relative h-full w-full" data-id="element-112">
      {/* Map Container - with improved fullscreen styling */}
      <div className="map-outer-container h-[600px] w-full rounded-lg overflow-hidden border border-gray-200 shadow-sm relative fullscreen-map-container" style={{
      position: 'relative',
      width: '100%',
      height: '600px'
    }} data-id="element-113">
        <style jsx data-id="element-114">{`
          .fullscreen-map-container:fullscreen,
          .fullscreen-map-container:-webkit-full-screen,
          .fullscreen-map-container:-moz-full-screen,
          .fullscreen-map-container:-ms-fullscreen {
            width: 100vw !important;
            height: 100vh !important;
            padding: 0 !important;
            margin: 0 !important;
            z-index: 9999 !important;
          }
          .fullscreen-map-container:fullscreen .leaflet-container,
          .fullscreen-map-container:-webkit-full-screen .leaflet-container,
          .fullscreen-map-container:-moz-full-screen .leaflet-container,
          .fullscreen-map-container:-ms-fullscreen .leaflet-container {
            height: 100vh !important;
            width: 100vw !important;
          }
          /* Fix for Safari and some browsers */
          :global(.leaflet-container) {
            height: 100% !important;
            width: 100% !important;
          }
          /* Ensure map takes full height in fullscreen */
          :fullscreen .leaflet-container,
          :-webkit-full-screen .leaflet-container,
          :-moz-full-screen .leaflet-container,
          :-ms-fullscreen .leaflet-container {
            height: 100vh !important;
          }
        `}</style>
        <MapContainer key="map-container" center={center} zoom={zoom} style={{
        height: '100%',
        width: '100%',
        zIndex: 10
      }} zoomControl={false} attributionControl={true} scrollWheelZoom={true} className="z-10" data-id="element-115">
          <MapController data-id="element-116" />
          <ResetMapView center={center} zoom={zoom} onToggleOperations={onToggleOperations} showOperations={showOperations} mapWrapperRef={mapWrapperRef} data-id="element-117" />
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' maxZoom={19} data-id="element-118" />
          {/* Driver routes */}
          {drivers.filter(driver => driver.status !== 'offline').map(driver => {
          // Find any orders assigned to this driver
          const driverOrders = orders.filter(order => order.driver?.id === `DRV-${driver.id}`);
          if (driverOrders.length === 0) return null;
          // For each driver with orders, draw routes to their assigned orders
          return driverOrders.map(order => <RouteDrawer key={`driver-route-${driver.id}-${order.id}`} startPoint={driver.location} endPoints={[order.location]} color="#ff5722" weight={4} opacity={0.7} onRouteCalculated={routeInfo => {
            driver.routeInfo = routeInfo;
          }} data-id="element-119" />);
        })}
          {/* Order routes - from pickup to all delivery locations */}
          {orders.map(order => {
          const deliveryCoordinates = order.deliveryLocations.map(loc => loc.coordinates);
          return <RouteDrawer key={`order-route-${order.id}`} startPoint={order.location} endPoints={deliveryCoordinates} color="#0097ff" weight={3} opacity={0.7} onRouteCalculated={routeInfo => handleRouteCalculated(order.id, routeInfo)} data-id="element-120" />;
        })}
          {/* Add driver markers with improved visibility */}
          {drivers.filter(driver => driver.status !== 'offline').map(driver => <Marker key={`driver-${driver.id}`} position={driver.location} icon={createVehicleIcon(driver.vehicle, driver.status)} eventHandlers={{
          click: () => onDriverSelect(driver)
        }} ref={marker => marker && registerMarkerRef(`driver-${driver.id}`, marker)} data-id="element-121">
                <Popup data-id="element-122">
                  <div className="p-2" data-id="element-123">
                    <div className="font-medium text-gray-900" data-id="element-124">
                      {driver.name}
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-125">
                      {getStatusText(driver.status)}
                    </div>
                    {driver.routeInfo && <div className="mt-1 text-xs text-gray-500" data-id="element-126">
                        <div className="flex items-center" data-id="element-127">
                          <ClockIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-128" />
                          <span data-id="element-129">ETA: {driver.routeInfo.duration} min</span>
                        </div>
                        <div className="flex items-center mt-0.5" data-id="element-130">
                          <MapPinIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-131" />
                          <span data-id="element-132">Distance: {driver.routeInfo.distance} km</span>
                        </div>
                      </div>}
                  </div>
                </Popup>
              </Marker>)}
          {/* Add order markers with multi-stop delivery support */}
          {orders.map(order => <Fragment key={order.id} data-id="element-133">
              {/* Order pickup marker */}
              <Marker position={order.location} icon={createOrderIcon(order.status)} eventHandlers={{
            click: () => handleOrderMarkerClick(order)
          }} ref={marker => marker && registerMarkerRef(`order-${order.id}`, marker)} data-id="element-134">
                <Popup data-id="element-135">
                  <div className="p-3" data-id="element-136">
                    <div className="font-medium text-gray-900 text-sm" data-id="element-137">
                      {order.id}
                    </div>
                    <div className="text-sm text-gray-600 mb-1" data-id="element-138">
                      {order.customer}
                    </div>
                    {orderRoutes[order.id] && <div className="mt-1 text-xs bg-gray-50 p-2 rounded-md" data-id="element-139">
                        <div className="font-medium text-gray-700 mb-0.5" data-id="element-140">
                          Route Summary
                        </div>
                        <div className="flex items-center" data-id="element-141">
                          <ClockIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-142" />
                          <span data-id="element-143">
                            Total ETA: {orderRoutes[order.id].duration} min
                          </span>
                        </div>
                        <div className="flex items-center mt-0.5" data-id="element-144">
                          <MapPinIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-145" />
                          <span data-id="element-146">
                            Total Distance: {orderRoutes[order.id].distance} km
                          </span>
                        </div>
                      </div>}
                    <button className="mt-2 text-xs bg-congress-blue-50 text-congress-blue-600 px-2 py-1 rounded-md hover:bg-congress-blue-100 w-full flex items-center justify-center" onClick={() => handleOrderMarkerClick(order)} data-id="element-147">
                      <PackageIcon size={12} className="mr-1" data-id="element-148" />
                      {language === 'en' ? 'View details' : 'عرض التفاصيل'}
                    </button>
                  </div>
                </Popup>
              </Marker>
              {/* Delivery location markers - now with multiple stops */}
              {order.deliveryLocations.map((location, index) => <Marker key={`delivery-${order.id}-${index}`} position={location.coordinates} icon={createDeliveryStopIcon(location.stopNumber || index + 1, !!location.completed)} eventHandlers={{
            click: () => handleStopMarkerClick(order.id, index)
          }} ref={marker => marker && registerMarkerRef(`delivery-${order.id}-${index}`, marker)} data-id="element-149">
                  <Popup data-id="element-150">
                    <div className="p-3" data-id="element-151">
                      <div className="font-medium text-gray-900 mb-1" data-id="element-152">
                        {language === 'en' ? `Stop ${location.stopNumber || index + 1} for ${order.id}` : `المحطة ${location.stopNumber || index + 1} ل ${order.id}`}
                      </div>
                      <div className="text-sm text-gray-600 mb-1" data-id="element-153">
                        {location.address}, {location.area}
                      </div>
                      {location.routeInfo && <div className="mt-2 text-xs bg-gray-50 p-2 rounded-md" data-id="element-154">
                          <div className="flex items-center" data-id="element-155">
                            <ClockIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-156" />
                            <span data-id="element-157">ETA: {location.routeInfo.eta} min</span>
                          </div>
                          <div className="flex items-center mt-0.5" data-id="element-158">
                            <MapPinIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-159" />
                            <span data-id="element-160">
                              Distance: {location.routeInfo.distance} km
                            </span>
                          </div>
                        </div>}
                      <div className="mt-2 flex items-center" data-id="element-161">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${location.completed ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-162">
                          {location.completed ? language === 'en' ? 'Completed' : 'مكتمل' : language === 'en' ? 'Pending' : 'قيد الانتظار'}
                        </span>
                      </div>
                    </div>
                  </Popup>
                </Marker>)}
            </Fragment>)}
        </MapContainer>
        {/* Delivery Operations Panel - now INSIDE the fullscreen wrapper */}
        <AnimatePresence data-id="element-163">
          {showOperations && <motion.div className="absolute top-6 left-6 z-[1001]" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.2
        }} style={{
          maxHeight: 'calc(100% - 48px)'
        }} data-id="element-164">
              <div className="bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden flex flex-col w-80" style={{
            maxHeight: isFullscreen ? 'calc(100vh - 48px)' : '520px'
          }} data-id="element-165">
                {/* Card Header */}
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-white sticky top-0 z-10" data-id="element-166">
                  <div className="flex items-center" data-id="element-167">
                    <LayoutDashboardIcon size={18} className="text-congress-blue-600 mr-2" data-id="element-168" />
                    <h3 className="font-medium text-gray-800" data-id="element-169">
                      {language === 'en' ? 'Delivery Operations' : 'عمليات التوصيل'}
                    </h3>
                  </div>
                  <div className="flex items-center space-x-1" data-id="element-170">
                    <Tooltip content={language === 'en' ? isCardCollapsed ? 'Expand' : 'Collapse' : isCardCollapsed ? 'توسيع' : 'طي'} data-id="element-171">
                      <button onClick={() => setIsCardCollapsed && setIsCardCollapsed(!isCardCollapsed)} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors" aria-label={language === 'en' ? isCardCollapsed ? 'Expand' : 'Collapse' : isCardCollapsed ? 'توسيع' : 'طي'} data-id="element-172">
                        {isCardCollapsed ? <ChevronDownIcon size={16} className="text-gray-500" data-id="element-173" /> : <ChevronUpIcon size={16} className="text-gray-500" data-id="element-174" />}
                      </button>
                    </Tooltip>
                    <Tooltip content={language === 'en' ? 'Close operations' : 'إغلاق العمليات'} data-id="element-175">
                      <button onClick={onToggleOperations} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors" aria-label={language === 'en' ? 'Close operations' : 'إغلاق العمليات'} data-id="element-176">
                        <XIcon size={16} className="text-gray-500" data-id="element-177" />
                      </button>
                    </Tooltip>
                  </div>
                </div>
                {/* Card Content - Collapsible */}
                <AnimatePresence data-id="element-178">
                  {!isCardCollapsed && <motion.div className="overflow-y-auto flex-1 overscroll-contain" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-179">
                      <div className="p-4" data-id="element-180">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-181">
                          <div className="relative overflow-hidden rounded-lg border border-congress-blue-100 shadow-sm transition-all hover:shadow-md" data-id="element-182">
                            <div className="absolute top-0 left-0 w-full h-1 bg-congress-blue-500" data-id="element-183"></div>
                            <div className="text-center p-4 bg-congress-blue-50" data-id="element-184">
                              <div className="text-2xl font-bold text-congress-blue-600" data-id="element-185">
                                <div to={120} data-id="element-186" />
                              </div>
                              <div className="text-sm text-gray-600 mt-1 flex items-center justify-center" data-id="element-187">
                                <TruckIcon size={14} className="mr-1.5 text-congress-blue-500" data-id="element-188" />
                                {language === 'en' ? 'Active Drivers' : 'السائقين النشطين'}
                              </div>
                            </div>
                          </div>
                          <div className="relative overflow-hidden rounded-lg border border-green-100 shadow-sm transition-all hover:shadow-md" data-id="element-189">
                            <div className="absolute top-0 left-0 w-full h-1 bg-green-500" data-id="element-190"></div>
                            <div className="text-center p-4 bg-green-50" data-id="element-191">
                              <div className="text-2xl font-bold text-green-600" data-id="element-192">
                                <div to={22} data-id="element-193" />
                              </div>
                              <div className="text-sm text-gray-600 mt-1 flex items-center justify-center" data-id="element-194">
                                <PackageIcon size={14} className="mr-1.5 text-green-500" data-id="element-195" />
                                {language === 'en' ? 'Active Orders' : 'الطلبات النشطة'}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Quick Actions */}
                        <div className="mb-6" data-id="element-196">
                          <h4 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider" data-id="element-197">
                            {language === 'en' ? 'Quick Actions' : 'إجراءات سريعة'}
                          </h4>
                          <QuickActions data-id="element-198" />
                        </div>
                        {/* Active Drivers */}
                        <div className="mb-3" data-id="element-199">
                          <h4 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider flex items-center" data-id="element-200">
                            <TruckIcon size={14} className="mr-1.5 text-congress-blue-500" data-id="element-201" />
                            {language === 'en' ? 'Active Drivers' : 'السائقين النشطين'}
                          </h4>
                          <div className="max-h-[220px] overflow-y-auto pr-1 rounded-md" data-id="element-202">
                            <DriverList onDriverSelect={onDriverSelect} data-id="element-203" />
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>}
        </AnimatePresence>
        {/* Delivery Status Panel with improved fullscreen visibility */}
        <AnimatePresence data-id="element-204">
          {showDeliveryStatus && selectedOrder && <motion.div className="absolute top-6 left-6 bg-white rounded-lg shadow-lg border border-gray-200 w-80 z-[1001] overflow-hidden max-h-[calc(100%-48px)]" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} style={{
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          zIndex: 1001
        }} data-id="element-205">
              {/* Card Header */}
              <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center flex-shrink-0" data-id="element-206">
                <div className="flex items-center" data-id="element-207">
                  <PackageIcon size={18} className="text-congress-blue-600 mr-2" data-id="element-208" />
                  <h3 className="font-medium text-gray-800" data-id="element-209">
                    {language === 'en' ? 'Order Tracking' : 'تتبع الطلب'}
                  </h3>
                </div>
                <div className="flex items-center space-x-1" data-id="element-210">
                  <Tooltip content={language === 'en' ? deliveryStatusCollapsed ? 'Expand' : 'Collapse' : deliveryStatusCollapsed ? 'توسيع' : 'طي'} data-id="element-211">
                    <button onClick={() => setDeliveryStatusCollapsed(!deliveryStatusCollapsed)} className="p-1.5 rounded-md hover:bg-gray-200 transition-colors" data-id="element-212">
                      {deliveryStatusCollapsed ? <ChevronDownIcon size={16} className="text-gray-500" data-id="element-213" /> : <ChevronUpIcon size={16} className="text-gray-500" data-id="element-214" />}
                    </button>
                  </Tooltip>
                  <Tooltip content={language === 'en' ? 'Close' : 'إغلاق'} data-id="element-215">
                    <button onClick={closeDeliveryStatus} className="p-1.5 rounded-md hover:bg-gray-200 transition-colors" data-id="element-216">
                      <XIcon size={16} className="text-gray-500" data-id="element-217" />
                    </button>
                  </Tooltip>
                </div>
              </div>
              {/* Card Content - Collapsible */}
              <AnimatePresence data-id="element-218">
                {!deliveryStatusCollapsed && <motion.div className="overflow-y-auto" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-219">
                    {/* Order Info */}
                    <div className="p-4 border-b border-gray-100" data-id="element-220">
                      <div className="flex justify-between items-start mb-3" data-id="element-221">
                        <div data-id="element-222">
                          <div className="text-sm text-gray-500" data-id="element-223">
                            {language === 'en' ? 'Order ID' : 'رقم الطلب'}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-224">
                            {selectedOrder.id}
                          </div>
                        </div>
                        <div className="text-right" data-id="element-225">
                          <div className="text-sm text-gray-500" data-id="element-226">
                            {language === 'en' ? 'Customer' : 'العميل'}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-227">
                            {selectedOrder.customer}
                          </div>
                        </div>
                      </div>
                      {/* Pickup location */}
                      <div className="mb-3" data-id="element-228">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-229">
                          {language === 'en' ? 'Pickup' : 'الاستلام'}
                        </div>
                        <div className="text-sm text-gray-700" data-id="element-230">
                          {selectedOrder.pickupLocation.address},{' '}
                          {selectedOrder.pickupLocation.area}
                        </div>
                      </div>
                      {/* Delivery locations - now supporting multiple stops with highlighting */}
                      <div data-id="element-231">
                        <div className="text-sm text-gray-500 mb-2" data-id="element-232">
                          {language === 'en' ? `Delivery Locations (${selectedOrder.deliveryLocations.length})` : `مواقع التوصيل (${selectedOrder.deliveryLocations.length})`}
                        </div>
                        {selectedOrder.deliveryLocations.map((location, index) => {
                    const isSelected = selectedStop && selectedStop.orderId === selectedOrder.id && selectedStop.stopIndex === index;
                    return <div key={index} ref={el => {
                      if (el) stopRefs.current[`stop-${selectedOrder.id}-${index}`] = el;
                    }} className={`text-sm mb-3 p-2 rounded-md cursor-pointer transition-all duration-200 ${isSelected ? 'bg-congress-blue-50 border border-congress-blue-200 shadow-sm' : 'hover:bg-gray-50'}`} onClick={() => handleSidebarStopClick(selectedOrder.id, index, location.coordinates)} data-id="element-233">
                                <div className="flex items-start" data-id="element-234">
                                  <div className="w-5 h-5 rounded-full bg-congress-blue-100 text-congress-blue-600 flex items-center justify-center mr-2 flex-shrink-0 text-xs font-medium mt-0.5" data-id="element-235">
                                    {location.stopNumber || index + 1}
                                  </div>
                                  <div className="flex-1" data-id="element-236">
                                    <div className="text-gray-700 font-medium" data-id="element-237">
                                      {location.address}, {location.area}
                                    </div>
                                    {location.routeInfo && <div className="mt-1 text-xs text-gray-500" data-id="element-238">
                                        <div className="flex items-center" data-id="element-239">
                                          <ClockIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-240" />
                                          <span data-id="element-241">
                                            ETA: {location.routeInfo.eta} min
                                          </span>
                                        </div>
                                        <div className="flex items-center mt-0.5" data-id="element-242">
                                          <MapPinIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-243" />
                                          <span data-id="element-244">
                                            Distance:{' '}
                                            {location.routeInfo.distance} km
                                          </span>
                                        </div>
                                      </div>}
                                    {location.completed && <div className="mt-1" data-id="element-245">
                                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-800" data-id="element-246">
                                          {language === 'en' ? 'Completed' : 'مكتمل'}
                                        </span>
                                      </div>}
                                  </div>
                                  {isSelected && <div className="ml-1 text-congress-blue-500" data-id="element-247">
                                      <MapPinIcon size={16} data-id="element-248" />
                                    </div>}
                                </div>
                              </div>;
                  })}
                      </div>
                      {/* Route information */}
                      {orderRoutes[selectedOrder.id] && <div className="mt-3 p-3 bg-gray-50 rounded-md" data-id="element-249">
                          <div className="text-sm font-medium text-gray-700 mb-1" data-id="element-250">
                            {language === 'en' ? 'Route Summary' : 'ملخص المسار'}
                          </div>
                          <div className="grid grid-cols-2 gap-2" data-id="element-251">
                            <div className="text-xs text-gray-600 flex items-center" data-id="element-252">
                              <ClockIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-253" />
                              {language === 'en' ? 'Total ETA: ' : 'الوقت المقدر الكلي: '}
                              <span className="font-medium ml-1" data-id="element-254">
                                {orderRoutes[selectedOrder.id].duration} min
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 flex items-center" data-id="element-255">
                              <MapPinIcon size={12} className="mr-1 text-congress-blue-500" data-id="element-256" />
                              {language === 'en' ? 'Total Distance: ' : 'المسافة الكلي: '}
                              <span className="font-medium ml-1" data-id="element-257">
                                {orderRoutes[selectedOrder.id].distance} km
                              </span>
                            </div>
                          </div>
                        </div>}
                    </div>
                    {/* Timeline - Collapsible */}
                    <div className="border-b border-gray-100" data-id="element-258">
                      <div className="p-4" data-id="element-259">
                        <div className="flex justify-between items-center mb-3" data-id="element-260">
                          <h4 className="text-sm font-medium text-gray-700" data-id="element-261">
                            {language === 'en' ? 'Delivery Timeline' : 'الجدول الزمني للتوصيل'}
                          </h4>
                          <button className="p-1.5 rounded-md hover:bg-gray-100 transition-colors flex items-center justify-center" onClick={() => {}} title={language === 'en' ? 'Refresh timeline' : 'تحديث الجدول الزمني'} data-id="element-262">
                            <RefreshCwIcon size={14} className="text-gray-500" data-id="element-263" />
                          </button>
                        </div>
                        {/* Total Delivery Time */}
                        <div className="flex items-center justify-between mb-1" data-id="element-264">
                          <div className="text-xs font-medium text-gray-500" data-id="element-265">
                            {language === 'en' ? 'Total Delivery Time' : 'إجمالي وقت التوصيل'}
                          </div>
                          <div className="text-xs font-bold text-gray-700" data-id="element-266">
                            {selectedOrder.totalTime || '14 hr 20 min'}
                          </div>
                        </div>
                        {/* Horizontal Progress Bar */}
                        <div className="mb-4" data-id="element-267">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-268">
                            <motion.div className="h-full rounded-full flex" initial={{
                        width: 0
                      }} animate={{
                        width: '100%'
                      }} transition={{
                        duration: 0.8,
                        ease: 'easeOut'
                      }} data-id="element-269">
                              {/* Generate segments based on timeline events */}
                              {selectedOrder.timeline.map((item, idx) => {
                          const speed = getDeliverySpeedFromStatus(item.status);
                          const width = `${100 / selectedOrder.timeline.length}%`;
                          return <div key={idx} className={`${getDeliverySpeedColor(speed)}`} style={{
                            width
                          }} data-id="element-270" />;
                        })}
                            </motion.div>
                          </div>
                          {/* Time markers */}
                          <div className="flex justify-between mt-1 text-xs text-gray-400" data-id="element-271">
                            {selectedOrder.timeline.length > 0 && <>
                                <div data-id="element-272">
                                  {selectedOrder.timeline[selectedOrder.timeline.length - 1].time}
                                </div>
                                {selectedOrder.timeline.length > 1 && <div data-id="element-273">{selectedOrder.timeline[0].time}</div>}
                              </>}
                          </div>
                        </div>
                        {/* Vertical Timeline */}
                        <div className="space-y-5 max-h-[220px] overflow-y-auto pr-1" data-id="element-274">
                          {selectedOrder.timeline.map((item, index) => <motion.div key={index} className="flex" initial={{
                      opacity: 0,
                      y: 10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: index * 0.1
                    }} data-id="element-275">
                              <div className="relative" data-id="element-276">
                                <div className={`
                                w-8 h-8 rounded-full flex items-center justify-center
                                ${index === 0 ? 'bg-congress-blue-50 border-2 border-congress-blue-500' : 'bg-gray-100 border-2 border-gray-300'}
                              `} data-id="element-277">
                                  {getTimelineIcon(item.status)}
                                </div>
                                {index !== selectedOrder.timeline.length - 1 && <div className="absolute top-8 left-4 bottom-0 w-0.5 bg-gray-200 -mb-5" data-id="element-278"></div>}
                              </div>
                              <div className="ml-4 flex-1" data-id="element-279">
                                <div className="flex items-center" data-id="element-280">
                                  <div className="font-medium text-gray-900" data-id="element-281">
                                    {item.title}
                                  </div>
                                  {index === 0 && <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-congress-blue-100 text-congress-blue-700" data-id="element-282">
                                      {language === 'en' ? 'Current' : 'الحالي'}
                                    </span>}
                                </div>
                                <div className="text-sm text-gray-500 mt-1 flex items-center" data-id="element-283">
                                  <ClockIcon size={12} className="mr-1 text-gray-400" data-id="element-284" />
                                  {item.time}, {item.date}
                                </div>
                                <div className="text-xs text-gray-500 mt-1 flex items-center" data-id="element-285">
                                  <MapPinIcon size={12} className="mr-1 text-gray-400" data-id="element-286" />
                                  {item.location}
                                </div>
                                {/* Delivery speed indicator */}
                                {item.status && <div className="mt-2 flex items-center" data-id="element-287">
                                    <div className={`
                                      w-2 h-2 rounded-full mr-1.5
                                      ${getDeliverySpeedColor(getDeliverySpeedFromStatus(item.status))}
                                    `} data-id="element-288"></div>
                                    <span className="text-xs text-gray-500" data-id="element-289">
                                      {getDeliverySpeedFromStatus(item.status) === 'fast' ? language === 'en' ? 'Fast delivery' : 'توصيل سريع' : getDeliverySpeedFromStatus(item.status) === 'slowed' ? language === 'en' ? 'Slowed down' : 'تباطأ' : language === 'en' ? 'Traffic jam' : 'ازدحام مروري'}
                                    </span>
                                  </div>}
                              </div>
                            </motion.div>)}
                        </div>
                      </div>
                    </div>
                    {/* Driver Info */}
                    {selectedOrder.driver && <div className="p-4 border-t border-gray-100 bg-gray-50" data-id="element-290">
                        <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-291">
                          {language === 'en' ? 'Driver Information' : 'معلومات السائق'}
                        </h4>
                        <div className="flex items-center" data-id="element-292">
                          <div className="w-10 h-10 rounded-full bg-congress-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-293">
                            <UserIcon size={20} className="text-congress-blue-600" data-id="element-294" />
                          </div>
                          <div className="ml-3" data-id="element-295">
                            <div className="font-medium text-gray-900" data-id="element-296">
                              {selectedOrder.driver.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-297">
                              {selectedOrder.driver.vehicle} •{' '}
                              {selectedOrder.driver.vehicleId}
                            </div>
                          </div>
                          <div className="ml-auto flex space-x-2" data-id="element-298">
                            <Tooltip content={language === 'en' ? 'Call Driver' : 'اتصال بالسائق'} data-id="element-299">
                              <button className="p-2 bg-congress-blue-50 text-congress-blue-600 rounded-full hover:bg-congress-blue-100 transition-colors" data-id="element-300">
                                <PhoneIcon size={16} data-id="element-301" />
                              </button>
                            </Tooltip>
                            <Tooltip content={language === 'en' ? 'Message Driver' : 'رسالة للسائق'} data-id="element-302">
                              <button className="p-2 bg-congress-blue-50 text-congress-blue-600 rounded-full hover:bg-congress-blue-100 transition-colors" data-id="element-303">
                                <MessageSquareIcon size={16} data-id="element-304" />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      </div>}
                  </motion.div>}
              </AnimatePresence>
            </motion.div>}
        </AnimatePresence>
        {/* Driver Info Card with improved fullscreen visibility */}
        <AnimatePresence data-id="element-305">
          {selectedDriver && <motion.div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 w-80 z-[1001] overflow-hidden" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.3
        }} style={{
          position: 'absolute',
          zIndex: 1001
        }} data-id="element-306">
              {/* Card Header with Status */}
              <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-start" data-id="element-307">
                <div className="flex items-center" data-id="element-308">
                  <div className="flex-shrink-0 relative" data-id="element-309">
                    {selectedDriver.avatar ? <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-congress-blue-100" data-id="element-310">
                        <img src={selectedDriver.avatar} alt={selectedDriver.name} className="w-full h-full object-cover" data-id="element-311" />
                      </div> : <div className="w-14 h-14 rounded-full bg-congress-blue-50 flex items-center justify-center border-2 border-congress-blue-100" data-id="element-312">
                        {selectedDriver.vehicle === 'car' ? <CarIcon size={28} className="text-congress-blue-500" data-id="element-313" /> : <div size={28} className="text-congress-blue-500" data-id="element-314" />}
                      </div>}
                    <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${getStatusColor(selectedDriver.status)} border-2 border-white`} data-id="element-315"></div>
                  </div>
                  <div className="ml-4" data-id="element-316">
                    <div className="font-medium text-gray-900 text-lg" data-id="element-317">
                      {selectedDriver.name}
                    </div>
                    <div className="flex items-center mt-1" data-id="element-318">
                      <div className={`h-2 w-2 rounded-full ${getStatusColor(selectedDriver.status)} mr-1.5`} data-id="element-319"></div>
                      <span className="text-sm text-gray-600" data-id="element-320">
                        {getStatusText(selectedDriver.status)}
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={() => onDriverSelect(null)} className="p-1.5 rounded-md hover:bg-gray-200 transition-colors" title={language === 'en' ? 'Close' : 'إغلاق'} data-id="element-321">
                  <XIcon size={16} className="text-gray-500" data-id="element-322" />
                </button>
              </div>
              {/* Driver Details */}
              <div className="p-4 space-y-3" data-id="element-323">
                {/* ID & Rating */}
                <div className="flex justify-between items-center" data-id="element-324">
                  <div className="text-sm text-gray-500" data-id="element-325">
                    <span className="font-medium text-gray-700" data-id="element-326">
                      {language === 'en' ? 'ID: ' : 'رقم التعريف: '}
                    </span>
                    {selectedDriver.id}
                  </div>
                  <div className="flex items-center" data-id="element-327">
                    <StarIcon size={16} className="text-yellow-400 mr-1" data-id="element-328" />
                    <span className="text-sm font-medium" data-id="element-329">
                      {selectedDriver.rating}
                    </span>
                  </div>
                </div>
                {/* Phone */}
                <div className="flex items-center text-sm text-gray-600" data-id="element-330">
                  <PhoneIcon size={14} className="mr-2 text-gray-400" data-id="element-331" />
                  <span data-id="element-332">{selectedDriver.phone || '+965 9876 1234'}</span>
                </div>
                {/* Location */}
                <div className="flex items-center text-sm text-gray-600" data-id="element-333">
                  <MapPinIcon size={14} className="mr-2 text-gray-400" data-id="element-334" />
                  <span data-id="element-335">
                    {selectedDriver.location ? `${language === 'en' ? 'Near ' : 'بالقرب من '}Kuwait City` : 'Kuwait City'}
                  </span>
                </div>
                {/* Orders Completed */}
                <div className="flex items-center text-sm text-gray-600" data-id="element-336">
                  <CheckCircleIcon size={14} className="mr-2 text-gray-400" data-id="element-337" />
                  <span data-id="element-338">
                    {selectedDriver.ordersCompleted || 0}
                    {language === 'en' ? ' orders completed' : ' طلبات مكتملة'}
                  </span>
                </div>
                {/* Route information if available */}
                {selectedDriver.routeInfo && <div className="mt-2 p-2 bg-gray-50 rounded-md" data-id="element-339">
                    <div className="text-xs font-medium text-gray-700 mb-1" data-id="element-340">
                      {language === 'en' ? 'Current Route' : 'المسار الحالي'}
                    </div>
                    <div className="grid grid-cols-2 gap-2" data-id="element-341">
                      <div className="text-xs text-gray-600" data-id="element-342">
                        <ClockIcon size={12} className="inline-block mr-1" data-id="element-343" />
                        {language === 'en' ? 'ETA: ' : 'الوقت المقدر: '}
                        <span className="font-medium" data-id="element-344">
                          {selectedDriver.routeInfo.duration} min
                        </span>
                      </div>
                      <div className="text-xs text-gray-600" data-id="element-345">
                        <MapPinIcon size={12} className="inline-block mr-1" data-id="element-346" />
                        {language === 'en' ? 'Distance: ' : 'المسافة: '}
                        <span className="font-medium" data-id="element-347">
                          {selectedDriver.routeInfo.distance} km
                        </span>
                      </div>
                    </div>
                  </div>}
              </div>
              {/* Action Buttons */}
              <div className="p-4 bg-gray-50 border-t border-gray-100" data-id="element-348">
                <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-349">
                  {/* Call Button */}
                  <button className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm" data-id="element-350">
                    <PhoneIcon size={16} className="mr-2 text-congress-blue-500" data-id="element-351" />
                    {language === 'en' ? 'Call' : 'اتصال'}
                  </button>
                  {/* Message Button */}
                  <button className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm" data-id="element-352">
                    <MessageSquareIcon size={16} className="mr-2 text-congress-blue-500" data-id="element-353" />
                    {language === 'en' ? 'Message' : 'رسالة'}
                  </button>
                </div>
                {/* Assign Order Button */}
                <button className="w-full flex items-center justify-center px-4 py-2 bg-regent-st-blue-600 text-white rounded-md text-sm font-medium hover:bg-regent-st-blue-700 transition-colors shadow-sm mb-3" data-id="element-354">
                  <UserPlusIcon size={16} className="mr-2" data-id="element-355" />
                  {language === 'en' ? 'Assign Order' : 'تعيين طلب'}
                </button>
                {/* Close Button */}
                <button className="w-full px-4 py-2 bg-congress-blue-600 text-white rounded-md text-sm font-medium hover:bg-congress-blue-700 transition-colors shadow-sm" onClick={() => onDriverSelect(null)} data-id="element-356">
                  {language === 'en' ? 'Close' : 'إغلاق'}
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
};